import React, { FC, useCallback, useEffect } from "react";

import { Col, Row, Stack } from "react-bootstrap";

import classNames from "classnames";
import dayjs from "dayjs";
import { ReactSVG } from "react-svg";
import { useResetRecoilState } from "recoil";

import {
  PaymentStatuses,
  type Resale,
  type ResaleFragment,
  ResaleStatuses,
  SortDirection,
  useListGroupedResalesQuery,
} from "@api/graphql";
import { DownloadIcon } from "@assets/icons";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { BatchActionButtons } from "@components/table/BatchActionButtons";
import type { TableHeader } from "@components/table/TableContents";
import type { TableFilterInput } from "@components/table/TableFilter";
import { useAuthenticationContext } from "@context/authentication";
import { ExternalRoles, Role } from "@roles";
import { selectedResales } from "@states/resale_submission";
import type { SortByTyped } from "@util/index";
import type { ResaleGroup } from "@util/resale";

import DocumentDropdown from "./DocumentDropdown";

import styles from "./PastReports.module.scss";

interface PastReportsAccordionHeaderProps {
  resales: Resale[];
}

const paymentStatusFilterOptions: TableFilterInput[] = [
  {
    option: PaymentStatuses.AMP_INVOICE_PAID,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.AMP_INVOICE_REQUESTED,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.ARTIST_PAID,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.ARTIST_PAYMENT_REQUESTED,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.CREDIT_NOTE_ISSUED,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.CREDIT_NOTE_REQUESTED,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.HOLD_PERIOD,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.INVOICE_REQUEST_SENT,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.NOT_APPLICABLE,
    defaultValue: false,
  },
  {
    option: PaymentStatuses.PENDING,
    defaultValue: false,
  },
  { option: PaymentStatuses.PAYMENT_REQUEST_SENT, defaultValue: false },
  { option: PaymentStatuses.HOLD_PERIOD, defaultValue: false },
];

const paymentStatusFilterOptionsVA: TableFilterInput[] = [
  { option: PaymentStatuses.AMP_INVOICE_PAID, defaultValue: true },
  { option: PaymentStatuses.AMP_INVOICE_REQUESTED, defaultValue: true },
  { option: PaymentStatuses.ARTIST_PAID, defaultValue: true },
  { option: PaymentStatuses.ARTIST_PAYMENT_REQUESTED, defaultValue: true },
  { option: PaymentStatuses.CREDIT_NOTE_ISSUED, defaultValue: true },
  { option: PaymentStatuses.CREDIT_NOTE_REQUESTED, defaultValue: true },
  { option: PaymentStatuses.NOT_APPLICABLE, defaultValue: true },
  { option: PaymentStatuses.PAYMENT_REQUEST_SENT, defaultValue: true },
  { option: PaymentStatuses.HOLD_PERIOD, defaultValue: true },
  {
    option: PaymentStatuses.RETURNED,
    defaultValue: true,
  },
  {
    option: PaymentStatuses.CLOSED,
    defaultValue: true,
  },
];

const useTableHeaders = (isVaAdmin: boolean, isDataTeam: boolean, isMSTeam: boolean): TableHeader<ResaleFragment>[] => {
  const tableHeaders: TableHeader<ResaleFragment>[] = [
    { label: "ID", accessor: "resale_id" },
    { label: "Artwork title", accessor: "artwork_name" },
    { label: "Artist", accessor: "artist_name" },
    { label: "Sold date", accessor: "date_sold" },
    { label: "Resale price", accessor: "resale_price" },
    ...(isDataTeam ? [{ label: "Royalty", accessor: "royalty" as keyof ResaleFragment }] : []),
  ];

  // Permission: only VA admin or MS able to see Finance status.
  if (isVaAdmin || isMSTeam) {
    tableHeaders.push({ label: "Finance status", accessor: "payment_status" });
  }

  // Permission: only VA admin or Data able to see Resale status
  if (isVaAdmin || isDataTeam) {
    tableHeaders.push({ label: "Resale status", accessor: "resale_status" });
  }

  return tableHeaders;
};

const PastReportsAccordionHeader: FC<PastReportsAccordionHeaderProps> = ({ resales }) => {
  // TODO: [NRRS-741] https://copyright.atlassian.net/browse/NRRS-741 remove when ready
  const show = false;
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const { auth } = useAuthenticationContext();
  return (
    <>
      <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />
      {show && (
        <Col sm={2}>
          <ProtectedComponent auth={auth}>
            <Stack>
              <Row>
                <Stack className={classNames(styles["report-type-csv"], "b4")} direction="horizontal" gap={2}>
                  <ReactSVG className={styles["download-icon"]} src={DownloadIcon} />
                  <span className={classNames(styles["file-name"])}>Invoice_012392.pdf</span>
                </Stack>
              </Row>
            </Stack>
            <Stack>
              <Row>
                <DocumentDropdown />
              </Row>
            </Stack>
          </ProtectedComponent>
        </Col>
      )}
    </>
  );
};

export const PastReports = () => {
  const isVaAdmin = useIsAuthUserAllowed([Role.VisualArtistAdmin]);
  const isDataTeam = useIsAuthUserAllowed([Role.Data]);
  const isMSTeam = useIsAuthUserAllowed([Role.MemberServices]);
  const resetSelectedResales = useResetRecoilState(selectedResales);
  const currentTimestamp = dayjs().endOf("day").toISOString();
  useEffect(() => {
    resetSelectedResales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // leave it blank so the selected resales got reset only once the page is mounted

  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };

  const filter_by = [
    ...(isVaAdmin || isDataTeam || isMSTeam
      ? [
          {
            field: "batch_group_confirmed",
            eq: "true",
          },
          {
            field: "opt_out_expiry",
            OR: [
              { field: "opt_out_expiry", lte: currentTimestamp },
              { field: "opt_out_expiry", eq: null as unknown as string },
              // null is not supported in appsync schema (will be ignored), casting to string to bypass
            ],
          },
          {
            field: "payment_status",
            in: paymentStatusFilterOptionsVA.map((option) => option.option),
          },
        ]
      : []),
  ];

  const resaleStatusFilterOptions: TableFilterInput[] = [
    {
      option: ResaleStatuses.CONFIRMED_ELIGIBLE,
      defaultValue: isVaAdmin,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_ELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.CONFIRMED_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.RESEARCH_REQUIRED,
      defaultValue: isVaAdmin,
    },
    {
      option: ResaleStatuses.OPTED_OUT,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.INVALID,
      defaultValue: false,
    },
  ];

  const getAccordionHeader = useCallback(
    ({ resales }: ResaleGroup) => <PastReportsAccordionHeader resales={resales} />,
    [],
  );

  const getTableToolbar = ({ batch_grouping_id, isProvisionalEligibleExist }: ResaleGroup, refetch?: () => void) => (
    <BatchActionButtons
      batchGroupingId={batch_grouping_id}
      isProvisionalEligibleExist={isProvisionalEligibleExist}
      displayDoneButton={false}
      displayIssueCreditNoteButton
      refetch={refetch}
    />
  );

  const rowCheckBoxIsDisabled = useCallback((row: ResaleFragment) => {
    return (
      row.payment_status !== PaymentStatuses.AMP_INVOICE_REQUESTED &&
      row.payment_status !== PaymentStatuses.AMP_INVOICE_PAID
    );
  }, []);

  return (
    <GroupedResales
      queryFunction={useListGroupedResalesQuery}
      getQueryVariables={(page) => ({
        input: { page, limit: 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
        input: {
          page,
          limit,
          filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
          sort_by,
        },
      })}
      getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
      getAccordionHeader={getAccordionHeader}
      customTableHeaders={useTableHeaders(isVaAdmin, isDataTeam, isMSTeam)}
      sourcePage={"PastReports"}
      resaleStatusFilterOptions={resaleStatusFilterOptions}
      paymentStatusFilterOptions={isVaAdmin ? paymentStatusFilterOptionsVA : paymentStatusFilterOptions}
      customTableStyleClassName={styles["customized-column-width"]}
      getTableToolbar={getTableToolbar}
      showCheckBox
      rowCheckBoxIsDisabled={rowCheckBoxIsDisabled}
    />
  );
};
